import * as React from "react";
import type { DashboardSummary } from "@excellent/core/server";
import {
  ProductDashboardHeader,
  type DashboardRangePreset,
} from "./product-dashboard-header";
import { DashboardRegions, type DashboardRegion } from "./dashboard-regions";

export type ProductDashboardRegion = DashboardRegion;

export interface ProductDashboardProps {
  /** The product slug, e.g. "crm". Used for region keys/aria, not lookup. */
  productName: string;
  /** Display label, e.g. "CRM". */
  label: string;
  /** One-line tagline from marketing.tagline / description. */
  tagline?: string;
  /**
   * Active project-scope name. When set, the header subtitle becomes an
   * operational context line ("Scoped to <project>") instead of the marketing
   * tagline — orientation/scope state belongs in the earliest fixation
   * sequence. See RESEARCH-CONVERGENCE-PLAN §A1.
   */
  scopeLabel?: string;
  /** Base path for range navigation, e.g. "/crm". */
  basePath: string;
  /** Primary action shown in the header strip. */
  primaryAction?: { label: string; href: string };
  /** Header strip override for callers that want a custom action area. */
  headerActions?: React.ReactNode;
  /** Date-range pill row appears when true. */
  timeScoped?: boolean;
  /** Current preset for the range pills; defaults to "30d". */
  currentRange?: DashboardRangePreset;
  /** Pre-computed dashboard payload from computeDashboardSummary. */
  summary: DashboardSummary;
  /** Regions to hide. Use sparingly; consistency is the goal. */
  hideRegions?: ProductDashboardRegion[];
  /**
   * Empty-state body for the activity region. Defaults to a generic line;
   * products surfacing inside a section pass a domain-specific one
   * (e.g. "Generated docs will appear here").
   */
  activityEmptyBody?: string;
  /**
   * Drop the outer page padding (`px-6 pt-5 pb-12`) and render a bare
   * `flex flex-col gap-5` stack. Use when the dashboard is embedded inside a
   * host that already owns its padding — e.g. a project-section overview page
   * via {@link SectionProductDashboard}. Default false (standalone page).
   */
  embedded?: boolean;
  /**
   * @deprecated Unused since the attention region became a quiet-when-empty
   * strip (it renders nothing when clear, so there is no empty-state body).
   * Kept for back-compat; safe to remove once no caller references it.
   */
  attentionEmptyBody?: string;
}

/**
 * The shared product-dashboard orchestrator. Renders five fixed regions
 * in a consistent layout:
 *
 *   1. Header (label + tagline + range pills + primary action)
 *   2. Stat grid (full width, on top)
 *   3. Two-column body:
 *      • LEFT (2fr): primary table/bars → insights beneath
 *      • RIGHT (1fr): recent activity → attention beneath
 *
 * Every Excellent product renders into this same shape. See
 * docs/dashboard/PRD.md §2 and §7.
 */
export function ProductDashboard({
  productName,
  label,
  tagline,
  scopeLabel,
  basePath,
  primaryAction,
  headerActions,
  timeScoped,
  currentRange,
  summary,
  hideRegions = [],
  activityEmptyBody,
  embedded = false,
}: ProductDashboardProps) {
  // Operational context wins the high-effort subtitle slot over the marketing
  // tagline when there's an active scope (research P11: subtitles cost more
  // reading effort than titles and should carry context, not decoration).
  const subtitle = scopeLabel ? `Scoped to ${scopeLabel}` : tagline;

  return (
    <div
      className={
        // Minimal design language spacing rhythm (docs/design/MINIMAL-SURFACES-PRD.md
        // §0): generous separation between the major bands (header / hero / stats /
        // attention / regions) now that surfaces are soft tints, not boxes.
        embedded ? "flex flex-col gap-10" : "flex flex-col gap-10 px-6 py-8"
      }
      data-product-dashboard={productName}
    >
      <ProductDashboardHeader
        label={label}
        tagline={subtitle}
        basePath={basePath}
        timeScoped={timeScoped}
        currentRange={currentRange}
        headerActions={headerActions}
        primaryActionLabel={primaryAction?.label}
        primaryActionHref={primaryAction?.href}
      />

      <DashboardRegions
        summary={summary}
        activityEmptyBody={activityEmptyBody}
        hideRegions={hideRegions}
      />
    </div>
  );
}
